/**
 * Constantes de l'application
 *
 * Centralise toutes les constantes utilisées dans l'application
 * pour faciliter la maintenance et la cohérence.
 */

/**
 * Mapping des stages Odoo vers les catégories du dashboard
 * Permet de regrouper les différents stages en 4 catégories principales
 */
export const STAGE_MAPPING = {
  'Backlog Produit': 'backlog',
  'Backlog Sprint': 'backlog',
  'Test': 'testing',
  'Test Client': 'clientTesting',
  'Done': 'done',
  'Done Product': 'done'
};

/**
 * Labels affichés pour chaque catégorie
 */
export const CATEGORY_LABELS = {
  backlog: 'Backlog',
  testing: 'En Test',
  clientTesting: 'Tests à valider',
  done: 'Done'
};

/**
 * Configuration des couleurs par catégorie
 * Utilisé pour la cohérence visuelle à travers l'application
 */
export const CATEGORY_COLORS = {
  backlog: {
    bg: '#F3F4F6',      // Fond gris clair
    text: '#6B7280',    // Texte gris
    bar: '#9CA3AF',     // Barre de progression gris
    border: '#E5E7EB',  // Bordure gris
    hover: '#E5E7EB'    // Hover gris
  },
  testing: {
    bg: '#DBEAFE',      // Fond bleu clair
    text: '#1E40AF',    // Texte bleu foncé
    bar: '#3B82F6',     // Barre de progression bleu
    border: '#93C5FD',  // Bordure bleu
    hover: '#BFDBFE'    // Hover bleu
  },
  clientTesting: {
    bg: '#FED7AA',      // Fond orange clair
    text: '#C2410C',    // Texte orange foncé
    bar: '#F97316',     // Barre de progression orange
    border: '#FDBA74',  // Bordure orange
    hover: '#FDE68A'    // Hover orange
  },
  done: {
    bg: '#D1FAE5',      // Fond vert clair
    text: '#065F46',    // Texte vert foncé
    bar: '#10B981',     // Barre de progression vert
    border: '#6EE7B7',  // Bordure vert
    hover: '#A7F3D0'    // Hover vert
  }
};

/**
 * Intervalle d'auto-refresh en millisecondes (5 minutes)
 */
export const AUTO_REFRESH_INTERVAL = 300000;

/**
 * Configuration des priorités de tâches
 */
export const TASK_PRIORITIES = {
  '0': {
    label: 'Aucune',
    color: '#9CA3AF',
    icon: 'circle'
  },
  '1': {
    label: 'Basse',
    color: '#10B981',
    icon: 'arrow-down'
  },
  '2': {
    label: 'Normale',
    color: '#3B82F6',
    icon: 'minus'
  },
  '3': {
    label: 'Haute',
    color: '#F97316',
    icon: 'arrow-up'
  },
  '4': {
    label: 'Urgente',
    color: '#EF4444',
    icon: 'alert-circle'
  }
};

/**
 * Statuts des jalons
 */
export const MILESTONE_STATUSES = {
  completed: {
    label: 'Atteint',
    color: '#10B981',
    bgColor: '#D1FAE5',
    icon: 'check-circle'
  },
  inprogress: {
    label: 'En cours',
    color: '#3B82F6',
    bgColor: '#DBEAFE',
    icon: 'clock'
  },
  upcoming: {
    label: 'À venir',
    color: '#6B7280',
    bgColor: '#F3F4F6',
    icon: 'calendar'
  },
  overdue: {
    label: 'En retard',
    color: '#EF4444',
    bgColor: '#FEE2E2',
    icon: 'alert-triangle'
  }
};

/**
 * Formats de date utilisés dans l'application
 */
export const DATE_FORMATS = {
  display: 'dd/MM/yyyy',
  displayWithTime: 'dd/MM/yyyy HH:mm',
  iso: 'yyyy-MM-dd',
  isoWithTime: 'yyyy-MM-dd HH:mm:ss',
  relative: 'PPP', // Format relatif (ex: 3 janvier 2024)
  short: 'dd/MM/yy'
};

/**
 * Messages d'erreur standardisés
 */
export const ERROR_MESSAGES = {
  auth: {
    invalidCredentials: 'Identifiants invalides',
    sessionExpired: 'Session expirée. Veuillez vous reconnecter.',
    unauthorized: 'Accès non autorisé',
    networkError: 'Erreur de connexion. Vérifiez votre connexion internet.'
  },
  data: {
    fetchError: 'Erreur lors de la récupération des données',
    noData: 'Aucune donnée disponible',
    invalidData: 'Données invalides',
    accessDenied: 'Accès refusé à ce projet'
  },
  export: {
    pdfError: 'Erreur lors de l\'export PDF',
    excelError: 'Erreur lors de l\'export Excel',
    csvError: 'Erreur lors de l\'export CSV'
  }
};

/**
 * Messages de succès standardisés
 */
export const SUCCESS_MESSAGES = {
  auth: {
    loginSuccess: 'Connexion réussie',
    logoutSuccess: 'Déconnexion réussie'
  },
  data: {
    refreshSuccess: 'Données actualisées',
    loadSuccess: 'Données chargées avec succès'
  },
  export: {
    pdfSuccess: 'Export PDF réussi',
    excelSuccess: 'Export Excel réussi',
    csvSuccess: 'Export CSV réussi'
  }
};

/**
 * Configuration de pagination
 */
export const PAGINATION = {
  defaultPageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
  maxPageButtons: 5
};

/**
 * Configuration des graphiques
 */
export const CHART_CONFIG = {
  colors: {
    primary: '#2563EB',
    secondary: '#737373',
    success: '#10B981',
    warning: '#F97316',
    danger: '#EF4444',
    info: '#3B82F6'
  },
  animation: {
    duration: 300,
    easing: 'ease-in-out'
  }
};

/**
 * Limites de l'application
 */
export const LIMITS = {
  maxProjectsPerClient: 100,
  maxTasksPerProject: 1000,
  maxExportRows: 5000,
  sessionTimeout: 86400000, // 24 heures
  refreshThreshold: 3600000 // 1 heure
};

/**
 * Configuration du localStorage
 */
export const STORAGE_KEYS = {
  auth: 'odoo_session',
  preferences: 'user_preferences',
  theme: 'theme_preference',
  lastProject: 'last_viewed_project'
};

/**
 * Routes de l'application
 */
export const ROUTES = {
  home: '/',
  login: '/login',
  dashboard: '/dashboard',
  project: '/project/:id',
  profile: '/profile',
  notFound: '/404'
};

const constants = {
  STAGE_MAPPING,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  AUTO_REFRESH_INTERVAL,
  TASK_PRIORITIES,
  MILESTONE_STATUSES,
  DATE_FORMATS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  PAGINATION,
  CHART_CONFIG,
  LIMITS,
  STORAGE_KEYS,
  ROUTES
};

export default constants;
